import { ArrowLeft, CalendarDays, MapPin, Phone, RefreshCw } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { EmptyState, PriceTag, Skeleton, StatusTimeline, toast } from "@aurelia/ui";
import { useInitiateSafepay, useOrder } from "../../features/orders/queries";
import { useDocumentMeta } from "../../lib/useDocumentMeta";
import { OrderProductRow } from "../../components/orders/OrderProductRow";
import { OrderReturnControl } from "../../components/orders/OrderReturnControl";
import { OrderStatusBadges } from "../../components/orders/OrderStatusBadges";
import { TrackingNumber } from "../../components/orders/TrackingNumber";
import { formatOrderDate, orderTotal, paymentMethodLabel } from "../../components/orders/orderPresentation";

function DetailsSkeleton() {
  return <div className="space-y-5"><Skeleton className="h-28" /><Skeleton className="h-32" /><Skeleton className="h-64" /></div>;
}

export function OrderDetailsPage() {
  useDocumentMeta("Order details", "Review your order details and delivery progress.", undefined, undefined, { noindex: true });
  const { id } = useParams();
  const orderQuery = useOrder(id);
  const initiateSafepay = useInitiateSafepay();

  if (orderQuery.isLoading) return <DetailsSkeleton />;
  if (orderQuery.isError || !orderQuery.data) {
    return (
      <EmptyState
        tone="error"
        title="We couldn't load this order"
        description="The order may be unavailable, or your connection may have been interrupted."
        action={<button type="button" onClick={() => orderQuery.refetch()} className="mt-1 inline-flex h-11 items-center gap-2 rounded-full bg-ink px-5 text-sm font-medium text-canvas dark:bg-accent dark:text-ink"><RefreshCw size={16} /> Retry</button>}
      />
    );
  }

  const order = orderQuery.data;
  const retryPayment = () => initiateSafepay.mutate(order.id, {
    onSuccess: (result) => window.location.assign(result.checkoutUrl),
    onError: () => toast("Safepay couldn't be opened. Please try again.", "danger")
  });

  return (
    <div className="min-w-0 space-y-5 sm:space-y-6">
      <Link to="/account/orders" className="inline-flex min-h-11 items-center gap-2 text-sm font-medium text-ink-muted hover:text-ink"><ArrowLeft size={17} /> Back to orders</Link>

      <section className="rounded-2xl border border-border bg-surface-raised p-4 shadow-soft sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wide text-ink-muted">Order details</p>
            <h1 className="mt-1 break-all text-2xl font-semibold sm:text-3xl">{order.orderNumber}</h1>
            <p className="mt-2 flex items-center gap-2 text-sm text-ink-muted"><CalendarDays size={16} /> Placed {formatOrderDate(order.createdAt)}</p>
          </div>
          <div className="sm:text-right"><p className="text-xs font-medium uppercase tracking-wide text-ink-muted">Total</p><PriceTag cents={orderTotal(order)} className="mt-1 text-2xl" /></div>
        </div>
        <div className="mt-5"><OrderStatusBadges order={order} /></div>
        <div className="mt-6 hidden overflow-x-auto pb-1 sm:block"><StatusTimeline status={order.status} /></div>
      </section>

      <section className="rounded-2xl border border-border bg-surface-raised p-4 sm:p-6">
        <h2 className="text-lg font-semibold">Delivery tracking</h2>
        <div className="mt-4 rounded-xl bg-surface px-4 py-3"><TrackingNumber trackingNumber={order.trackingNumber} status={order.status} /></div>
        {order.statusHistory.length > 0 && (
          <ol className="mt-5 space-y-3 border-l border-border pl-4">
            {[...order.statusHistory].reverse().map((entry, index) => (
              <li key={entry.id} className="relative text-sm">
                <span className={`absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full ${index === 0 ? "bg-accent" : "bg-border"}`} />
                <p className="font-medium">{entry.status[0] + entry.status.slice(1).toLowerCase()}</p>
                <p className="text-xs text-ink-muted">{formatOrderDate(entry.createdAt)}{entry.note ? ` · ${entry.note}` : ""}</p>
              </li>
            ))}
          </ol>
        )}
      </section>

      <section className="overflow-hidden rounded-2xl border border-border bg-surface-raised p-4 sm:p-6">
        <h2 className="text-lg font-semibold">Products</h2>
        <div className="mt-2 divide-y divide-border">
          {order.items.map((item) => <OrderProductRow key={item.id} item={item}><OrderReturnControl order={order} item={item} /></OrderProductRow>)}
        </div>
      </section>

      <div className="grid gap-5 lg:grid-cols-2">
        <section className="rounded-2xl border border-border bg-surface-raised p-4 sm:p-6">
          <h2 className="text-lg font-semibold">Payment & pricing</h2>
          <dl className="mt-4 space-y-3 text-sm">
            <div className="flex justify-between gap-4"><dt className="text-ink-muted">Payment method</dt><dd className="text-right font-medium">{paymentMethodLabel(order)}</dd></div>
            <div className="flex justify-between gap-4"><dt className="text-ink-muted">Items subtotal</dt><dd><PriceTag cents={order.subtotal} className="text-sm" /></dd></div>
            {order.discountAmount > 0 && <div className="flex justify-between gap-4 text-emerald-600"><dt>Discount{order.discountCodeSnapshot ? ` (${order.discountCodeSnapshot})` : ""}</dt><dd>-<PriceTag cents={order.discountAmount} className="text-sm" /></dd></div>}
            {order.coinsUsed > 0 && <div className="flex justify-between gap-4 text-emerald-600"><dt>Wallet credit ({order.coinsUsed} coins)</dt><dd>-<PriceTag cents={order.coinsUsed * 100} className="text-sm" /></dd></div>}
            <div className="flex justify-between gap-4"><dt className="text-ink-muted">Shipping</dt><dd><PriceTag cents={order.shippingFee} className="text-sm" /></dd></div>
            <div className="flex justify-between gap-4 border-t border-border pt-3 text-base font-semibold"><dt>Final total</dt><dd><PriceTag cents={orderTotal(order)} className="text-base" /></dd></div>
          </dl>
          {order.paymentMethod === "SAFEPAY" && order.paymentStatus !== "PAID" && order.status !== "CANCELLED" && (
            <button type="button" disabled={initiateSafepay.isPending} onClick={retryPayment} className="mt-5 h-11 w-full rounded-full border border-accent px-5 text-sm font-medium text-accent-strong hover:bg-accent-soft disabled:opacity-40">{initiateSafepay.isPending ? "Opening Safepay…" : "Retry Safepay payment"}</button>
          )}
        </section>

        <section className="rounded-2xl border border-border bg-surface-raised p-4 sm:p-6">
          <h2 className="text-lg font-semibold">Delivery information</h2>
          <div className="mt-4 flex items-start gap-3 text-sm"><MapPin size={18} className="mt-0.5 shrink-0 text-accent" /><address className="min-w-0 break-words not-italic leading-relaxed text-ink-muted"><span className="text-ink">{order.shippingAddress.line1}</span>{order.shippingAddress.line2 && <><br />{order.shippingAddress.line2}</>}<br />{order.shippingAddress.city}, {order.shippingAddress.province} {order.shippingAddress.postalCode}<br />{order.shippingAddress.country}</address></div>
          <div className="mt-4 flex items-center gap-3 text-sm"><Phone size={18} className="shrink-0 text-accent" /><span className="break-all">{order.contactPhone}</span></div>
        </section>
      </div>
    </div>
  );
}
